/**
 * ArraySampler - Utility for sampling arrays
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for taking samples from arrays,
 * which is useful for logging, debugging, and display purposes.
 */

/**
 * Get a sample of items from an array
 * 
 * Returns the first N items from an array. Useful for logging or displaying
 * a preview of array contents without showing the entire array.
 * 
 * @param array - Array to sample
 * @param count - Number of items to return (default: 3)
 * @returns Array containing the first N items
 */
export function sampleArray<T>(array: T[], count: number = 3): T[] {
  return array.slice(0, count);
}

/**
 * Get a sample of entries from a Map
 * 
 * Converts Map entries to an array and returns the first N entries.
 * Useful for logging Map contents without showing everything.
 * 
 * @param map - Map to sample
 * @param count - Number of entries to return (default: 3)
 * @returns Array containing the first N entries as [key, value] tuples
 */
export function sampleMapEntries<K, V>(map: Map<K, V>, count: number = 3): Array<[K, V]> {
  return Array.from(map.entries()).slice(0, count);
}

/**
 * Get a sample of keys from a Map
 * 
 * Returns the first N keys from a Map.
 * 
 * @param map - Map to sample
 * @param count - Number of keys to return (default: 3)
 * @returns Array containing the first N keys
 */
export function sampleMapKeys<K, V>(map: Map<K, V>, count: number = 3): K[] {
  return Array.from(map.keys()).slice(0, count);
}

/**
 * Get a sample of values from a Map
 * 
 * Returns the first N values from a Map.
 * 
 * @param map - Map to sample
 * @param count - Number of values to return (default: 3)
 * @returns Array containing the first N values
 */
export function sampleMapValues<K, V>(map: Map<K, V>, count: number = 3): V[] {
  return Array.from(map.values()).slice(0, count);
}

